// utils/auth.js - Utilidades de autenticación
const TOKEN_KEY = 'galaxy_access_token';
const REFRESH_TOKEN_KEY = 'galaxy_refresh_token';
const USER_KEY = 'galaxy_user';

// Funciones básicas de tokens
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (token) => localStorage.setItem(TOKEN_KEY, token);
export const removeToken = () => localStorage.removeItem(TOKEN_KEY);

export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);
export const setRefreshToken = (token) => localStorage.setItem(REFRESH_TOKEN_KEY, token);

// Funciones de usuario
export const getUser = () => {
  const userStr = localStorage.getItem(USER_KEY);
  return userStr ? JSON.parse(userStr) : null;
};

export const setUser = (user) => {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

// Función para limpiar toda la autenticación
export const clearAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

// Verificar si el usuario está autenticado
export const isAuthenticated = () => {
  const token = getToken();
  const refreshToken = getRefreshToken();
  return !!(token && refreshToken);
};

// **NUEVAS FUNCIONES PARA JWT Y REFRESH PROACTIVO**

// Decodificar JWT sin verificar (solo para leer datos)
export const decodeJWT = (token) => {
  try {
    if (!token) return null;
    
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const payload = parts[1];
    // Agregar padding si es necesario
    const padded = payload + '='.repeat((4 - payload.length % 4) % 4);
    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch (error) {
    console.warn('Error decodificando JWT:', error);
    return null;
  }
};

// Verificar si un token está próximo a expirar (menos de 5 minutos)
export const isTokenExpiringSoon = (token, minutesBeforeExpiry = 5) => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return true;
  
  const now = Math.floor(Date.now() / 1000);
  const timeUntilExpiry = decoded.exp - now;
  const minutesUntilExpiry = timeUntilExpiry / 60;
  
  return minutesUntilExpiry <= minutesBeforeExpiry;
};

// Obtener tiempo restante del token en minutos
export const getTokenTimeLeft = (token) => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return 0;
  
  const now = Math.floor(Date.now() / 1000);
  const timeUntilExpiry = decoded.exp - now;
  return Math.max(0, Math.floor(timeUntilExpiry / 60));
};

// Verificar si el token ha expirado
export const isTokenExpired = (token) => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return true;
  
  const now = Math.floor(Date.now() / 1000);
  return decoded.exp <= now;
};

// Obtener IDs de clientes asignados del token
export const getAssignedClientIds = () => {
  const token = getToken();
  if (!token) return [];
  
  const decoded = decodeJWT(token);
  if (!decoded) return [];
  
  return decoded.assignedClientIds || [];
};

// Variable global para el timer de refresh proactivo
let refreshTimer = null;

// Configurar refresh automático proactivo
export const setupProactiveRefresh = (onRefreshNeeded) => {
  clearProactiveRefresh(); // Limpiar timer anterior
  
  const checkAndScheduleRefresh = () => {
    const token = getToken();
    if (!token) return;
    
    const timeLeft = getTokenTimeLeft(token);
    
    if (timeLeft <= 5) {
      // Si quedan 5 minutos o menos, refrescar ahora
      onRefreshNeeded();
    } else if (timeLeft > 5) {
      // Programar refresh para 5 minutos antes de expirar
      const refreshIn = (timeLeft - 5) * 60 * 1000; // convertir a millisegundos
      
      refreshTimer = setTimeout(() => {
        onRefreshNeeded();
      }, refreshIn);
      

    }
  };
  
  // Verificar inmediatamente y programar
  checkAndScheduleRefresh();
};

// Limpiar el timer de refresh proactivo
export const clearProactiveRefresh = () => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
};

// **FUNCIONES DE ROLES Y PERMISOS (existentes)**

// Verificar si el usuario tiene un rol específico
export const hasRole = (requiredRole) => {
  const user = getUser();
  if (!user || !user.roles) return false;
  
  // Si el usuario es super admin, tiene acceso a todo
  if (user.isSuperAdmin) return true;
  
  // Verificar si tiene el rol específico (case-insensitive)
  const hasRequiredRole = user.roles.some(role => {
    // Manejar tanto strings como objetos de rol
    const roleName = typeof role === 'string' ? role : (role.name || role);
    const required = typeof requiredRole === 'string' ? requiredRole : requiredRole.name;
    
    const matches = roleName.toLowerCase() === required.toLowerCase();
    

    
    return matches;
  });
  
  return hasRequiredRole;
};

// Verificar si el usuario tiene acceso a un área de negocio específica
export const hasBusinessAreaAccess = (businessAreaCode) => {
  const user = getUser();
  if (!user) return false;
  
  // Si el usuario es super admin, tiene acceso a todo
  if (user.isSuperAdmin) return true;
  
  // Si no hay áreas de negocio definidas, denegar acceso
  if (!user.businessAreas || user.businessAreas.length === 0) return false;
  
  // Verificar si tiene acceso al área específica
  return user.businessAreas.some(area => area === businessAreaCode);
};

// Obtener roles del usuario
export const getUserRoles = () => {
  const user = getUser();
  return user?.roles || [];
};

// Obtener áreas de negocio del usuario
export const getUserBusinessAreas = () => {
  const user = getUser();
  return user?.businessAreas || [];
};
  
// Verificar si el usuario es super administrador
export const isSuperAdmin = () => {
  const user = getUser();
  if (!user) return false;
  
  // Verificar si el usuario tiene la flag isSuperAdmin o el rol SUPER_ADMIN
  if (user.isSuperAdmin) return true;
  
  // También verificar por el rol SUPER_ADMIN en la lista de roles (case-insensitive)
  if (user.roles && Array.isArray(user.roles)) {
    return user.roles.some(role => {
      const roleName = typeof role === 'string' ? role : (role.name || role);
      return roleName.toLowerCase() === 'super_admin';
    });
  }
  
  return false;
}; 